import React, { memo, useState } from "react";
import { useDispatch } from "react-redux";
import clsx from "clsx";
import makeStyles from "@mui/styles/makeStyles";
import CreateIcon from "@mui/icons-material/Create";

import { palette } from "../../theme/palette";
import { ROUTE_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING } from "../../consts/strings";

import { addNotification } from "../../reducers/ownApp";
import { setSelectedRoute } from "../../reducers/ownApp/";

import { editRoute } from "../../reducers/routeManagement/thunkActions";

import { BinIcon } from "../Icons";
import { View } from "../View";
import { RemoveRouteModal } from "../Modals/RemoveRoute/RemoveRouteModal";
import { RouteCardContent } from "./RouteCardContent";
import RouteLabel from "./RouteLabel";

const useStyles = makeStyles(() => {
  return {
    route: {
      display: "flex",
      flexDirection: "column",
      background: palette.typography.white.neutral,
      padding: "0.625rem",
      paddingBottom: "0.5rem",
      cursor: "default",
      height: (props) => (props.height || 1) - 1,
    },
    highlighted: {
      background: palette.typography.white.disabled,
    },
    editIconBase: {
      backgroundColor: (props) =>
        props.editing
          ? palette.orange
          : props.notInWave
          ? "#30303044"
          : "#303030",
      borderRadius: "50%",
      width: "24px",
      height: "24px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    editIcon: {
      color: palette.typography.white.solid,
      width: "13px",
      height: "13px",
      cursor: "pointer",
    },
    binIcon: {
      marginTop: "10px",
      fill: palette.typography.white.solid,
      height: "20px",
      width: "20px",
      cursor: "pointer",
    },
    top: {
      display: "flex",
      flexDirection: "row",
      justifyContent: "space-between",
      marginRight: "0.25rem",
    },
    topTitle: {
      color: palette.white,
      fontSize: 12,
    },
    topSpace: {
      width: "0.8rem",
    },
    middle: {
      display: "flex",
      flexDirection: "row",
      gap: "0.3rem",
    },
    leftButtons: {
      width: "2.5rem",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      marginRight: "auto",
    },
    right: {
      flex: 1,
      display: "flex",
      flexDirection: "column",
      marginLeft: "0.5rem",
      color: "white",
      lineHeight: "1.25rem",
      gap: "0.25rem",
    },
    bottom: {
      margin: "0.25rem 0rem",
      display: "flex",
      gap: "0.5rem",
      fontSize: 12,
      color: palette.white,
      paddingTop: "0.25rem",
      borderWidth: "1px",
      borderTopStyle: "solid",
      borderColor: palette.white,
    },
    bottomLeft: {
      display: "flex",
      flexDirection: "column",
      gap: "0.2rem",
      width: "9rem",
      textAlign: "right",
    },
    bottomRight: {
      display: "flex",
      flexDirection: "column",
      gap: "0.2rem",
      width: "5rem",
      textAlign: "center",
    },
  };
});

export const RouteCardBase = ({
  height,
  route,
  editMode,
  inEdit,
  selected,
}) => {
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const dispatch = useDispatch();
  const styles = useStyles({ editing: inEdit, height });

  const handleClick = (e) => {
    e.preventDefault();
    if (editMode)
      dispatch(
        addNotification({
          message: ROUTE_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(route),
          options: { variant: "info" },
        })
      );
    else if (selected) dispatch(setSelectedRoute(null));
    else dispatch(setSelectedRoute(route));
  };

  const handleEdit = (e) => {
    e.stopPropagation();
    if (editMode && !inEdit)
      dispatch(
        addNotification({
          message: ROUTE_MANAGEMENT_EDIT_MODE_ACTIVE_WARNING(route),
          options: { variant: "info" },
        })
      );
    else if (!editMode) dispatch(editRoute(route));
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setShowRemoveModal(true);
  };

  return (
    <div style={{ height }}>
      <View
        className={clsx(
          styles.route,
          (inEdit || selected) && styles.highlighted
        )}
        onClick={handleClick}
      >
        <View className={styles.top}>
          <RouteLabel route={route} />
          <View className={styles.topSpace} />
          <View className={styles.topTitle}>Description</View>
          <View className={styles.topTitle}>Urban MPH</View>
          <View className={styles.topTitle}>Rural MPH</View>
        </View>
        <View className={styles.middle}>
          <View className={styles.leftButtons}>
            <View className={styles.editIconBase} onClick={handleEdit}>
              <CreateIcon fontSize={"small"} className={styles.editIcon} />
            </View>
            <BinIcon className={styles.binIcon} onClick={handleClear} />
          </View>

          <RouteCardContent route={route} />
        </View>
        <View className={styles.bottom}>
          <View className={styles.bottomLeft}>
            <View>Additional minutes: </View>
            <View>Use toll: </View>
            <View>Use ferry: </View>
          </View>
          <View className={styles.bottomRight}>
            <View>{route.additionalMinutes}</View>
            <View>{route.useToll ? "Yes" : "No"}</View>
            <View>{route.useFerry ? "Yes" : "No"}</View>
          </View>
        </View>
      </View>
      {showRemoveModal && (
        <RemoveRouteModal
          route={route}
          onClose={() => setShowRemoveModal(false)}
        />
      )}
    </div>
  );
};

export default memo(RouteCardBase);
